import {
  ApiMutualFundAssetRead,
  ApiSegregatedPortfolioAssetRead
} from '../../../../../../api/rtk-api'
import {
  Break,
  Fees,
  FieldSet,
  FormItem,
  Label,
  ProtectionAssetSelector,
  RadioWithLabel,
  Section,
  SliderWithTextInput,
  Space,
  ValidationMessage
} from '../../../../../../components/core-v2'
import { Errors } from '../../../../util'
import { StyledCharges } from '../../../01-note/existing-note/details-summary/primitives'
import { StyledChargesBlock } from '../../primitives'
import { LeverageState } from '../../state/strategy-slice'

export type LeverageEditorProps = {
  assets?: (ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]
  errors: Errors
  isGeneratingCharts: boolean
  onChangeConstantProportionLeverageRatio: (value: string) => void
  onChangeTargetVolatility: (value: string) => void
  onChangeVolControlLeverageRatio: (value: string) => void
  onInspectAsset: (assetId: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => void
  onSelectAsset: (asset: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead) => void
  setType: (type: 'constant-proportion' | 'vol-control') => void
  state: LeverageState
}

export function LeverageEditorDumb({
  assets,
  errors,
  isGeneratingCharts: isGeneratingChart,
  onChangeConstantProportionLeverageRatio,
  onChangeTargetVolatility,
  onChangeVolControlLeverageRatio,
  onInspectAsset,
  onSelectAsset,
  setType,
  state
}: LeverageEditorProps) {
  return (
    <fieldset disabled={isGeneratingChart}>
      <Space mx={5} my={6}>
        <FormItem>
          <Label>Choose a type of leverage</Label>
          <FieldSet direction="horizontal">
            {state.constantProportion.isAllowed && (
              <RadioWithLabel
                checked={state.type === 'constant-proportion'}
                data-testid="option-constant-proportion"
                onChange={() => setType('constant-proportion')}
              >
                Constant Proportion
              </RadioWithLabel>
            )}
            {state.volControl.isAllowed && (
              <RadioWithLabel
                checked={state.type === 'vol-control'}
                data-testid="option-vol-control"
                onChange={() => setType('vol-control')}
              >
                Volatility Control
              </RadioWithLabel>
            )}
          </FieldSet>
        </FormItem>

        {state.type === 'constant-proportion' && (
          <Space mt={6}>
            <Space mb={6}>
              <FormItem>
                <Label>Leverage ratio</Label>
                <SliderWithTextInput
                  data-testid="leverage-ratio"
                  max={state.constantProportion.leverageRatio.max}
                  min={state.constantProportion.leverageRatio.min}
                  step={0.025}
                  value={state.constantProportion.leverageRatio.value}
                  onChange={value => onChangeConstantProportionLeverageRatio(value)}
                />
              </FormItem>
            </Space>

            {Array.isArray(state.constantProportion.fees) && (
              <Space mb={6} mt={4}>
                <StyledChargesBlock>
                  <Section type="secondary">
                    <StyledCharges>
                      <Fees fees={state.constantProportion.fees} title="Charges" />
                    </StyledCharges>
                  </Section>
                </StyledChargesBlock>
              </Space>
            )}
          </Space>
        )}

        {state.type === 'vol-control' && (
          <Space mt={6}>
            <Space mb={5}>
              <FormItem>
                <Label>Leverage ratio</Label>
                <SliderWithTextInput
                  data-testid="leverage-ratio"
                  max={state.volControl.leverageRatio.max}
                  min={state.volControl.leverageRatio.min}
                  step={0.025}
                  value={state.volControl.leverageRatio.value}
                  onChange={value => onChangeVolControlLeverageRatio(value)}
                />
              </FormItem>
            </Space>
            <Space mb={6}>
              <FormItem>
                <Label>Target volatility (%)</Label>
                <SliderWithTextInput
                  data-testid="target-volatility"
                  max={state.volControl.targetVolatility.max}
                  min={state.volControl.targetVolatility.min}
                  step={0.25}
                  value={state.volControl.targetVolatility.value}
                  onChange={value => onChangeTargetVolatility(value)}
                />
              </FormItem>
            </Space>
            <Space pb={6}>
              <Break />
            </Space>
            <ProtectionAssetSelector
              asset={state.volControl.protectionAsset}
              assets={assets}
              onInspectAsset={onInspectAsset}
              onSelectAsset={asset => onSelectAsset(asset)}
            />
            <ValidationMessage errors={errors} fieldName="leverage_volControl_protectionAsset" />

            {Array.isArray(state.volControl.fees) && (
              <Space my={6}>
                <StyledChargesBlock>
                  <Section type="secondary">
                    <StyledCharges>
                      <Fees fees={state.volControl.fees} title="Charges" />
                    </StyledCharges>
                  </Section>
                </StyledChargesBlock>
              </Space>
            )}
          </Space>
        )}
      </Space>
    </fieldset>
  )
}
